import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { MessageSquareText } from "lucide-react";
import { getCourseTeacherFeedback } from "../../Action/courseAction";
import { CustomAdminTable, CustomBadge, CustomPageLayout } from "../../Component/customComponents";
import { RatingBadge } from "../Common/ratingCommon";

// opened from Teacher Feedback with { id: recordedLectureId, val: lectureName, teacherName }
function TeacherLectureFeedback() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const state = useLocation().state || {};
  const lectureId = state.id != null ? String(state.id) : "";

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (!lectureId) return; // the old page crashed when opened directly
    setLoading(true);
    dispatch(
      getCourseTeacherFeedback(
        lectureId,
        (res) => {
          setFeedback(Array.isArray(res?.data) ? res.data : []);
          setLoading(false);
        },
        () => setLoading(false),
      ),
    );
  }, []);

  // average of the ratings (as before)
  const average = useMemo(
    () => (feedback.length ? feedback.reduce((t, f) => t + (Number(f?.rating) || 0), 0) / feedback.length : 0),
    [feedback],
  );

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return feedback;
    return feedback.filter((f) =>
      [f?.userFirstName, f?.userLastName, f?.subjectName, f?.topicName, f?.feedback]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [feedback, searchText]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const columns = [
    {
      key: "student",
      header: "Student",
      minWidth: 170,
      render: (_, row) => (
        <div>
          <strong>{[row?.userFirstName, row?.userLastName].filter(Boolean).join(" ") || "—"}</strong>
          {row?.userMobileNo && <div className="text-muted small">{row.userMobileNo}</div>}
        </div>
      ),
    },
    { key: "rating", header: "Rating", align: "center", render: (v) => <RatingBadge value={v} /> },
    { key: "subjectName", header: "Subject", minWidth: 120, render: (v) => v || "—" },
    { key: "topicName", header: "Topic", minWidth: 120, render: (v) => v || "—" },
    { key: "feedback", header: "Feedback", minWidth: 280, wrap: true, render: (v) => v || "—" },
    { key: "createdAt", header: "Date", minWidth: 150, render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY LTS") : "—") },
  ];

  return (
    <CustomPageLayout
      title="LECTURE FEEDBACK"
      subtitle={`Lecture: ${state.val || "—"} · Teacher: ${state.teacherName?.name || "—"}`}
      isGoBack
      onBack={() => navigate(-1)}
    >
      <CustomAdminTable
        id="lecture-feedback-table"
        title={`Feedback (${feedback.length})`}
        columns={columns}
        data={paged}
        loading={loading}
        rowKey={(row, index) => row?._id || index}
        enableSerialNumber
        serialHeader="#"
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search student, topic or feedback...",
        }}
        toolbarActions={
          <>
            <CustomBadge tone="info" icon={<MessageSquareText size={12} />}>
              Reviews: {feedback.length}
            </CustomBadge>
            <RatingBadge value={average} size="md" />
          </>
        }
        pagination={{
          page,
          pageSize,
          total: filtered.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={lectureId ? (searchText ? "No matches" : "No feedback") : "Nothing to show"}
        emptyDescription={
          lectureId
            ? searchText
              ? "Try a different search."
              : "No student feedback on this lecture yet."
            : "Open this page from Teacher Feedback by choosing a lecture."
        }
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
}

export default TeacherLectureFeedback;
